import type { ExplicacaoMetrica } from '../components/MetricExplanationTooltip';

/** Fallback conservador quando Monte Carlo não executou */
export const PROB_ATRASO_FALLBACK = 0.5;

export function explicacaoExposicao(bac: number, ev: number): ExplicacaoMetrica {
  const exposicao = Math.max(0, bac - ev);
  return {
    titulo: 'Exposição Real',
    formula: 'Exposição = BAC − EV',
    descricao: 'Saldo remanescente do contrato ainda não executado. Representa o capital total ainda sujeito ao desempenho futuro da obra.',
    componentes: {
      'BAC (orçamento total)': bac,
      'EV (valor agregado)': ev,
      'Exposição Real': exposicao,
    },
  };
}

export function explicacaoCapitalEmRisco(bac: number, ev: number, probAtraso: number | null): ExplicacaoMetrica {
  const exposicao = Math.max(0, bac - ev);
  const usedFallback = probAtraso === null || probAtraso === undefined;
  const prob = usedFallback ? PROB_ATRASO_FALLBACK : probAtraso;
  const capital = Math.max(0, Math.min(exposicao * prob, exposicao));
  return {
    titulo: 'Capital em Risco',
    formula: 'Capital em Risco = Exposição × Prob. Atraso',
    descricao: 'Estimativa da parcela da exposição sujeita a variabilidade, baseada em simulação probabilística de prazo.',
    componentes: {
      'Exposição Real': exposicao,
      'Prob. atraso': usedFallback
        ? `${(prob * 100).toFixed(0)}% (conservador — sem simulação)`
        : `${(prob * 100).toFixed(1)}%`,
      'Capital em Risco': capital,
    },
  };
}

export function explicacaoCapitalProtegido(bac: number, ev: number, score: number | null): ExplicacaoMetrica {
  const exposicao = Math.max(0, bac - ev);
  const s = score ?? 0;
  const protegido = Math.max(0, Math.min(exposicao * (s / 100), exposicao));
  return {
    titulo: 'Capital Protegido Operacionalmente',
    formula: 'Capital Protegido = Exposição × (Score / 100)',
    descricao: 'Parcela da exposição com maior previsibilidade operacional, baseada em indicadores de execução, qualidade e prazo. Não representa garantia ou seguro financeiro.',
    componentes: {
      'Exposição Real': exposicao,
      'Score operacional': score !== null ? `${s.toFixed(0)}/100` : '— (sem dados)',
      'Capital Protegido': protegido,
    },
  };
}

export function explicacaoIndiceCapitalQualificado(scorePonderado: number, totalExposicao: number): ExplicacaoMetrica {
  return {
    titulo: 'Índice de Capital Qualificado',
    formula: 'ICQ = Σ(Score × Exposição) / Σ(Exposição)',
    descricao: 'Média ponderada do score operacional pela exposição de cada contrato. Traduz a qualidade operacional da carteira em uma métrica fiduciária percentual.',
    componentes: {
      'ICQ': `${scorePonderado.toFixed(1)}%`,
      'Exposição total': totalExposicao,
    },
  };
}

export function explicacaoSPI(ev: number | null, pv: number | null): ExplicacaoMetrica {
  const spi = (pv && pv > 0 && ev != null) ? ev / pv : null;
  return {
    titulo: 'SPI (Schedule Performance Index)',
    formula: 'SPI = EV / PV',
    descricao: 'Índice de desempenho de prazo. SPI < 1 indica atraso em relação ao planejado. SPI > 1 indica adiantamento.',
    componentes: {
      'EV (valor agregado)': ev,
      'PV (valor planejado)': pv,
      'SPI': spi !== null ? spi.toFixed(3) : '— (sem baseline)',
    },
  };
}

export function explicacaoProbAtraso(probAtraso: number | null): ExplicacaoMetrica {
  return {
    titulo: 'Probabilidade de Atraso',
    formula: 'P(atraso) = 1 − P(no prazo)',
    descricao: 'Probabilidade de o contrato não concluir dentro do prazo contratual, derivada de simulação Monte Carlo com 5.000 iterações.',
    componentes: {
      'Prob. atraso': probAtraso !== null ? `${(probAtraso * 100).toFixed(1)}%` : '— (sem simulação)',
    },
  };
}
